import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customersApi, pensionFundsApi, toolsApi, fondationsApi } from '../../services/api';
import { ArrowLeft, FileText, Download, Trash2, Eye, Info, X, ArrowRight, CheckCircle, Hourglass, AlertCircle, Search, CheckCheck, ExternalLink, Edit2, User, Phone, Mail, Building2, Calendar } from 'lucide-react';
import type { Customer, PensionFund, MultiFondationDocument } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import CustomerInfoTab from './CustomerInfoTab';
import CustomerDocumentsTab from './CustomerDocumentsTab';
import CustomerActivityTab from './CustomerActivityTab';

const stateNames: { [key: number]: string } = {
  1: 'Contr\u00f4le administratif',
  2: 'Soumission institutions',
  3: 'Attente r\u00e9ponses',
  4: 'Dossier cl\u00f4tur\u00e9'
};

const stateColors: { [key: number]: string } = {
  1: 'bg-blue-100 text-blue-800',
  2: 'bg-emerald-100 text-emerald-800',
  3: 'bg-amber-100 text-amber-800',
  4: 'bg-green-100 text-green-800'
};

const CustomerDetails: React.FC = () => {
  const notify = useNotification();
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'info' | 'documents' | 'caisses' | 'activity' | 'workflow'>('info');
  const [formData, setFormData] = useState<Partial<Customer>>({});
  const [selectedPensionFund, setSelectedPensionFund] = useState<PensionFund | null>(null);
  const [isEditingState, setIsEditingState] = useState(false);
  const [newState, setNewState] = useState<number>(1);
  const [regenerateDocuments, setRegenerateDocuments] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [skipSuccessAlert, setSkipSuccessAlert] = useState(false);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState<'contrat_regroupement' | 'instruction' | null>(null);
  const [newDocumentFondation, setNewDocumentFondation] = useState('');
  const [newDocumentFile, setNewDocumentFile] = useState<File | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['customer', uuid],
    queryFn: async () => {
      const response = await customersApi.getDetails(uuid!);
      return response.data.data;
    },
    enabled: !!uuid,
    staleTime: 0, // Always refetch when navigating to ensure fresh data
    refetchOnMount: 'always', // Refetch when component mounts
  });

  // Sync formData with fetched data
  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  // Check if any editable fields have changed
  const hasChanges = useMemo(() => {
    if (!data) return false;
    const editableFields = [
      'nom', 'prenom', 'sexe', 'dat', 'avs', 'tel', 'login',
      'adr', 'npa', 'loc', 'pays', 'sit_pro', 'profession',
      'civil', 'date_civil', 'souhait'
    ];
    return editableFields.some(field => {
      const originalValue = (data as unknown as Record<string, unknown>)[field] ?? '';
      const currentValue = (formData as unknown as Record<string, unknown>)[field] ?? '';
      return originalValue !== currentValue;
    });
  }, [data, formData]);

  // Fetch pension funds for this customer
  const { data: pensionFundsData, isLoading: isPensionFundsLoading } = useQuery({
    queryKey: ['customer-pension-funds', uuid],
    queryFn: async () => {
      const response = await pensionFundsApi.getAll({
        search: data?.full_name || '',
        per_page: 100,
      });
      // Filter by customer_uuid to ensure we only get this customer's pension funds
      const filtered = response.data.data.filter((pf: PensionFund) => pf.customer_uuid === uuid);
      return filtered;
    },
    enabled: !!uuid && !!data && activeTab === 'caisses',
  });

  // Fetch activities for the selected pension fund
  const { data: pensionFundActivities } = useQuery({
    queryKey: ['pension-fund-history', selectedPensionFund?.uuid],
    queryFn: async () => {
      if (!selectedPensionFund?.uuid) return [];
      const response = await pensionFundsApi.getDetails(selectedPensionFund.uuid);
      return response.data.data.activities || [];
    },
    enabled: !!selectedPensionFund?.uuid,
  });

  // Fetch company fondations for the add document modal
  const { data: companyFondations, isLoading: isLoadingFondations } = useQuery({
    queryKey: ['company-fondations', data?.company_id],
    queryFn: async () => {
      if (!data?.company_id) return [];
      const response = await fondationsApi.getForCompany(data.company_id);
      return response.data.data;
    },
    enabled: !!data?.company_id && showAddDocumentModal !== null,
  });

  // Filter out fondations that already have a document for the selected type
  const availableFondations = useMemo(() => {
    if (!companyFondations || !showAddDocumentModal || !data) return [];

    const existingFondations = showAddDocumentModal === 'contrat_regroupement'
      ? (data.contrat_regroupement_documents || []).map((doc: MultiFondationDocument) => doc.fondation_name)
      : (data.instruction_documents || []).map((doc: MultiFondationDocument) => doc.fondation_name);

    return companyFondations.filter((fondation: string) => !existingFondations.includes(fondation));
  }, [companyFondations, showAddDocumentModal, data]);

  const updateInfoMutation = useMutation({
    mutationFn: (data: Partial<Customer>) =>
      customersApi.updateInfo(uuid!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', uuid] });
      queryClient.invalidateQueries({ queryKey: ['customer-history', uuid] });
      // Only show alert if not regenerating documents (to avoid double confirmation)
      if (!skipSuccessAlert) {
        notify.success('Informations mises \u00e0 jour avec succ\u00e8s');
      }
      setSkipSuccessAlert(false);
    },
    onError: (error: unknown) => {
      console.error('Update error:', error);
      const errObj = error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } }; message?: string };
      console.error('Error response:', errObj.response?.data);

      // Check for validation errors
      if (errObj.response?.data?.errors) {
        const validationErrors = Object.entries(errObj.response.data.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('; ');
        notify.error(`Erreur de validation: ${validationErrors}`);
      } else {
        notify.error(`Erreur lors de la mise \u00e0 jour: ${errObj.response?.data?.message || errObj.message || 'Erreur inconnue'}`);
      }
    },
  });

  const uploadDocumentMutation = useMutation({
    mutationFn: (formData: FormData) =>
      customersApi.uploadDocument(uuid!, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', uuid] });
      queryClient.invalidateQueries({ queryKey: ['customer-history', uuid] });
      notify.success('Document t\u00e9l\u00e9charg\u00e9 avec succ\u00e8s');
    },
    onError: (error: unknown) => {
      console.error('Upload error:', error);
      notify.error('Erreur lors du t\u00e9l\u00e9chargement du document');
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: ({ documentType }: { documentType: string }) =>
      customersApi.uploadDocument(uuid!, (() => {
        const formData = new FormData();
        formData.append('type', documentType);
        formData.append('delete', '1');
        return formData;
      })()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', uuid] });
      queryClient.invalidateQueries({ queryKey: ['customer-history', uuid] });
      notify.success('Document supprim\u00e9 avec succ\u00e8s');
    },
    onError: (error: unknown) => {
      console.error('Delete error:', error);
      notify.error('Erreur lors de la suppression du document');
    },
  });

  const updateStateMutation = useMutation({
    mutationFn: (state: number) =>
      customersApi.updateState(uuid!, state),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', uuid] });
      queryClient.invalidateQueries({ queryKey: ['customer-history', uuid] });
      setIsEditingState(false);
      notify.success('\u00c9tat mis \u00e0 jour avec succ\u00e8s');
    },
    onError: (error: unknown) => {
      console.error('State update error:', error);
      const errObj = error as { response?: { data?: { message?: string } }; message?: string };
      notify.error(`Erreur lors de la mise \u00e0 jour de l'\u00e9tat: ${errObj.response?.data?.message || errObj.message || 'Erreur inconnue'}`);
    },
  });

  const handleFileUpload = (field: string, file: File) => {
    if (!file) return;

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      notify.error('Le fichier est trop volumineux. Taille maximale: 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      notify.error('Type de fichier non autoris\u00e9. Formats accept\u00e9s: PDF, JPG, JPEG, PNG');
      return;
    }

    const formData = new FormData();
    formData.append('type', field);
    formData.append('file', file);
    uploadDocumentMutation.mutate(formData);
  };

  const handleFileDelete = (field: string, documentName: string) => {
    const confirmDelete = window.confirm(
      `\u00cates-vous s\u00fbr de vouloir supprimer ${documentName}?`
    );

    if (confirmDelete) {
      deleteDocumentMutation.mutate({ documentType: field });
    }
  };

  const handleAddMultiFondationDocument = () => {
    if (!showAddDocumentModal || !newDocumentFondation.trim() || !newDocumentFile) {
      notify.error('Veuillez remplir tous les champs');
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append('type', `${showAddDocumentModal}:${newDocumentFondation.trim()}`);
    formDataObj.append('file', newDocumentFile);
    uploadDocumentMutation.mutate(formDataObj, {
      onSuccess: () => {
        setShowAddDocumentModal(null);
        setNewDocumentFondation('');
        setNewDocumentFile(null);
      }
    });
  };

  const getFileUrl = (filePath: string | undefined) => {
    if (!filePath) return '';
    // Use the files API URL from environment
    const filesApiUrl = process.env.REACT_APP_FILES_API_URL || 'http://127.0.0.1:8010/api/files';
    return `${filesApiUrl}?path=${encodeURIComponent(filePath)}`;
  };

  const handleInputChange = (field: keyof Customer, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveInfo = async () => {
    console.log('Sending data:', formData);
    console.log('Regenerate documents:', regenerateDocuments);

    try {
      // If regenerating, skip the success alert from mutation (we'll show our own)
      if (regenerateDocuments) {
        setSkipSuccessAlert(true);
      }

      // First update customer info
      await updateInfoMutation.mutateAsync(formData);

      // If regenerate documents is checked, call the regenerate endpoint
      if (regenerateDocuments && uuid) {
        setIsRegenerating(true);
        try {
          const response = await toolsApi.regenerateDocuments(uuid);
          console.log('Documents regenerated:', response.data);
          notify.success('Informations mises \u00e0 jour et documents r\u00e9g\u00e9n\u00e9r\u00e9s avec succ\u00e8s');
          // Refresh customer data to get new document URLs
          queryClient.invalidateQueries({ queryKey: ['customer', uuid] });
        } catch (regenError: unknown) {
          console.error('Document regeneration error:', regenError);
          const errObj = regenError as { response?: { data?: { message?: string } }; message?: string };
          notify.error(`Informations mises \u00e0 jour, mais erreur lors de la r\u00e9g\u00e9n\u00e9ration des documents: ${errObj.response?.data?.message || errObj.message || 'Erreur inconnue'}`);
        } finally {
          setIsRegenerating(false);
          setRegenerateDocuments(false);
        }
      }
    } catch (error) {
      // Error already handled by mutation
    }
  };

  const handleStateChange = () => {
    const confirmChange = window.confirm(
      `\u00cates-vous s\u00fbr de vouloir changer l'\u00e9tat de "${stateNames[data?.state || 1]}" vers "${stateNames[newState]}"?`
    );

    if (confirmChange) {
      updateStateMutation.mutate(newState);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <p className="text-red-600 font-medium">Erreur lors du chargement du client</p>
        <p className="text-gray-500 text-sm mt-1">{(error as Error)?.message || 'Une erreur est survenue'}</p>
        <button
          onClick={() => navigate('/customers')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retour \u00e0 la liste
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
        <p className="text-gray-600 font-medium">Client non trouv\u00e9</p>
        <button
          onClick={() => navigate('/customers')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retour \u00e0 la liste
        </button>
      </div>
    );
  }

  const tabs: Array<{ id: 'info' | 'documents' | 'caisses' | 'activity' | 'workflow'; label: string }> = [
    { id: 'info', label: 'Informations' },
    { id: 'documents', label: 'Documents' },
    { id: 'caisses', label: 'Caisses' },
    { id: 'activity', label: 'Activit\u00e9' },
    { id: 'workflow', label: 'Workflow' },
  ];

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/customers')}
            className="mr-4 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{data.full_name}</h1>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-gray-600">{data.company_name || 'Soci\u00e9t\u00e9 non renseign\u00e9e'}</span>
              <span className="text-gray-400">{'\u2022'}</span>
              <span className="text-gray-600">{data.agent_name || 'Agent non renseign\u00e9'}</span>
              <span className="text-gray-400">{'\u2022'}</span>
              {data.intervention_conseiller ? (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500 text-white">
                  Intervention conseiller
                </span>
              ) : (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stateColors[data.state] || 'bg-gray-100 text-gray-800'}`}>
                  {stateNames[data.state] || '\u00c9tat inconnu'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Customer Info Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex items-center">
            <User className="w-5 h-5 text-gray-400 mr-3" />
            <div>
              <p className="text-xs text-gray-500">AVS</p>
              <p className="text-sm font-medium text-gray-900">{data.avs || 'Non renseign\u00e9'}</p>
            </div>
          </div>
          <div className="flex items-center">
            <Phone className="w-5 h-5 text-gray-400 mr-3" />
            <div>
              <p className="text-xs text-gray-500">{`T\u00e9l\u00e9phone`}</p>
              <p className="text-sm font-medium text-gray-900">{data.tel || 'Non renseign\u00e9'}</p>
            </div>
          </div>
          <div className="flex items-center">
            <Building2 className="w-5 h-5 text-gray-400 mr-3" />
            <div>
              <p className="text-xs text-gray-500">Fondation</p>
              <p className="text-sm font-medium text-gray-900">{data.fondation || 'Non renseign\u00e9'}</p>
            </div>
          </div>
          <div className="flex items-center">
            <Calendar className="w-5 h-5 text-gray-400 mr-3" />
            <div>
              <p className="text-xs text-gray-500">Date de naissance</p>
              <p className="text-sm font-medium text-gray-900">{data.dat || 'Non renseign\u00e9'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recherche & Regroupement Badges */}
      <div className="flex items-center justify-end gap-3 mb-4">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
          data.recherche
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-500'
        }`}>
          {data.recherche ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <X className="w-4 h-4" />
          )}
          Recherche
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
          data.regroupement
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-500'
        }`}>
          {data.regroupement ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <X className="w-4 h-4" />
          )}
          Regroupement
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'info' && (
        <CustomerInfoTab
          formData={formData}
          hasChanges={hasChanges}
          regenerateDocuments={regenerateDocuments}
          isRegenerating={isRegenerating}
          isSaving={updateInfoMutation.isPending}
          onInputChange={handleInputChange}
          onRegenerateDocumentsChange={setRegenerateDocuments}
          onSave={handleSaveInfo}
        />
      )}

      {activeTab === 'documents' && (
        <CustomerDocumentsTab
          formData={formData}
          data={data}
          isUploadPending={uploadDocumentMutation.isPending}
          isDeletePending={deleteDocumentMutation.isPending}
          getFileUrl={getFileUrl}
          onFileUpload={handleFileUpload}
          onFileDelete={handleFileDelete}
          showAddDocumentModal={showAddDocumentModal}
          onShowAddDocumentModal={setShowAddDocumentModal}
          newDocumentFondation={newDocumentFondation}
          onNewDocumentFondationChange={setNewDocumentFondation}
          newDocumentFile={newDocumentFile}
          onNewDocumentFileChange={setNewDocumentFile}
          onAddMultiFondationDocument={handleAddMultiFondationDocument}
          isLoadingFondations={isLoadingFondations}
          availableFondations={availableFondations}
        />
      )}

      {activeTab === 'caisses' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900">Caisses de pension</h2>
          </div>

          {isPensionFundsLoading ? (
            <div className="text-center py-8 text-gray-500">Chargement...</div>
          ) : pensionFundsData && pensionFundsData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Caisse
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fondation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Montant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pensionFundsData.map((pensionFund: PensionFund) => {
                    const statusColors: Record<string, string> = {
                      'Nouveau': 'bg-gray-100 text-gray-800',
                      'En cours': 'bg-blue-100 text-blue-800',
                      'Rejet\u00e9': 'bg-red-100 text-red-800',
                      'Annul\u00e9': 'bg-orange-100 text-orange-800',
                      'Transf\u00e9r\u00e9': 'bg-green-100 text-green-800',
                      'Termin\u00e9': 'bg-green-100 text-green-800',
                    };
                    const colorClass = statusColors[pensionFund.status] || 'bg-gray-100 text-gray-800';

                    return (
                      <tr key={pensionFund.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {pensionFund.fund_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {pensionFund.fondation || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass}`}>
                            {pensionFund.status || 'Non d\u00e9fini'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          CHF {parseFloat(pensionFund.amount || '0').toLocaleString('fr-CH')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/pension-funds/${pensionFund.uuid}`)}
                              className="p-1.5 text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-lg transition"
                              title="Voir les d\u00e9tails"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setSelectedPensionFund(pensionFund)}
                              className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition"
                              title="Voir l'historique"
                            >
                              <Info className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">Aucune caisse de pension trouv\u00e9e pour ce client</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'activity' && <CustomerActivityTab uuid={uuid!} />}

      {activeTab === 'workflow' && (
        <div className="space-y-6">
          {/* Workflow Summary Card */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-lg border-2 border-blue-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">{`R\u00e9sum\u00e9 du Workflow`}</h2>
                <button
                  onClick={() => navigate(`/workflow/customers/${uuid}`)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ouvrir dans Workflow
                </button>
              </div>

              {/* Current State Display */}
              <div className="bg-white rounded-xl p-5 shadow-md mb-4">
                <div className="flex items-center gap-4">
                  {/* State Icon */}
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    data.state === 1 ? 'bg-blue-100' :
                    data.state === 2 ? 'bg-green-100' :
                    data.state === 3 ? 'bg-amber-100' :
                    data.state === 4 ? 'bg-emerald-100' : 'bg-gray-100'
                  }`}>
                    {data.state === 1 && <Search className={`w-8 h-8 text-blue-600`} />}
                    {data.state === 2 && <CheckCircle className={`w-8 h-8 text-green-600`} />}
                    {data.state === 3 && <Hourglass className={`w-8 h-8 text-amber-600`} />}
                    {data.state === 4 && <CheckCheck className={`w-8 h-8 text-emerald-600`} />}
                  </div>

                  {/* State Info */}
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-500 mb-1">{`\u00c9tat actuel`}</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                      {data.state === 1 && 'Contr\u00f4le administratif'}
                      {data.state === 2 && 'Soumission institutions'}
                      {data.state === 3 && 'Attente r\u00e9ponses'}
                      {data.state === 4 && 'Dossier cl\u00f4tur\u00e9'}
                      {!data.state && `\u00c9tat ${data.state || 'inconnu'}`}
                    </h3>
                    {data.subStatus && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                        {data.subStatus}
                      </span>
                    )}
                  </div>

                  {/* State Badge */}
                  <div className={`px-4 py-2 rounded-lg font-bold text-white ${
                    data.state === 1 ? 'bg-blue-600' :
                    data.state === 2 ? 'bg-green-600' :
                    data.state === 3 ? 'bg-amber-600' :
                    data.state === 4 ? 'bg-emerald-600' : 'bg-gray-600'
                  }`}>
                    {`\u00c9tat ${data.state}`}
                  </div>
                </div>
              </div>

              {/* Intervention Alert */}
              {data.intervention_conseiller && (
                <div className="bg-orange-100 border-2 border-orange-300 rounded-xl p-4 mb-4 flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-orange-900 mb-1">Intervention en cours</h4>
                    <p className="text-sm text-orange-800">
                      {`Ce client n\u00e9cessite une intervention d'un conseiller. Le workflow est en pause.`}
                    </p>
                  </div>
                </div>
              )}

              {/* What's Happening Section */}
              <div className="bg-white rounded-xl p-5 shadow-md">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-600" />
                  Que se passe-t-il ?
                </h4>
                <div className="space-y-2 text-sm text-gray-700">
                  {data.state === 1 && (
                    <>
                      <p className="flex items-start gap-2">
                        <span className="text-blue-600 mt-0.5">{'\u2022'}</span>
                        <span>{`Les donn\u00e9es et documents du client sont en cours de v\u00e9rification par l'\u00e9quipe administrative.`}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-blue-600 mt-0.5">{'\u2022'}</span>
                        <span>{`Une fois la v\u00e9rification termin\u00e9e, le dossier sera valid\u00e9 et passera \u00e0 l'\u00e9tape suivante.`}</span>
                      </p>
                    </>
                  )}
                  {data.state === 2 && (
                    <>
                      <p className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">{'\u2022'}</span>
                        <span>{`La v\u00e9rification est termin\u00e9e avec succ\u00e8s. Le dossier est valid\u00e9.`}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-green-600 mt-0.5">{'\u2022'}</span>
                        <span>{`Prochaine \u00e9tape : Upload de la demande suppl\u00e9tive et soumission aux institutions.`}</span>
                      </p>
                    </>
                  )}
                  {data.state === 3 && (
                    <>
                      <p className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">{'\u2022'}</span>
                        <span>{`Le dossier a \u00e9t\u00e9 envoy\u00e9 aux institutions (centrale de compensation et suppl\u00e9tive).`}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">{'\u2022'}</span>
                        <span>{`Nous attendons les r\u00e9ponses des institutions pour traiter les caisses de pension.`}</span>
                      </p>
                      {data.reponse_suppletive && (
                        <p className="flex items-start gap-2 font-medium text-amber-700">
                          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{`R\u00e9ponse suppl\u00e9tive re\u00e7ue - pr\u00eate \u00e0 \u00eatre trait\u00e9e`}</span>
                        </p>
                      )}
                      {data.etat && (
                        <p className="flex items-start gap-2 font-medium text-blue-700">
                          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{`R\u00e9ponse centrale re\u00e7ue - pr\u00eate \u00e0 \u00eatre trait\u00e9e`}</span>
                        </p>
                      )}
                    </>
                  )}
                  {data.state === 4 && (
                    <>
                      <p className="flex items-start gap-2">
                        <span className="text-emerald-600 mt-0.5">{'\u2022'}</span>
                        <span>{`Le traitement administratif est termin\u00e9.`}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="text-emerald-600 mt-0.5">{'\u2022'}</span>
                        <span>{`Les caisses de pension ont \u00e9t\u00e9 cr\u00e9\u00e9es et le dossier est archiv\u00e9.`}</span>
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Action Button */}
              <div className="mt-6">
                <button
                  onClick={() => navigate(`/workflow/customers/${uuid}`)}
                  className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl group"
                >
                  <span>{`G\u00e9rer le workflow de ce client`}</span>
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>

          {/* State Modification Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{`Modifier l'\u00e9tat du client`}</h3>
              <Edit2 className="w-5 h-5 text-gray-400" />
            </div>

            {!isEditingState ? (
              <button
                onClick={() => {
                  setIsEditingState(true);
                  setNewState(data.state);
                }}
                className="w-full px-4 py-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Edit2 className="w-4 h-4" />
                {`Modifier l'\u00e9tat`}
              </button>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {`Nouvel \u00e9tat`}
                  </label>
                  <select
                    value={newState}
                    onChange={(e) => setNewState(Number(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value={1}>{`\u00c9tat 1 - Contr\u00f4le administratif`}</option>
                    <option value={2}>{`\u00c9tat 2 - Soumission institutions`}</option>
                    <option value={3}>{`\u00c9tat 3 - Attente r\u00e9ponses`}</option>
                    <option value={4}>{`\u00c9tat 4 - Dossier cl\u00f4tur\u00e9`}</option>
                  </select>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleStateChange}
                    disabled={updateStateMutation.isPending || newState === data.state}
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
                  >
                    {updateStateMutation.isPending ? 'Mise \u00e0 jour...' : 'Confirmer'}
                  </button>
                  <button
                    onClick={() => setIsEditingState(false)}
                    disabled={updateStateMutation.isPending}
                    className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{`\u00c9tat actuel:`}</span>
                <span className="font-semibold text-gray-900">{`\u00c9tat ${data.state}`}</span>
              </div>
            </div>
          </div>

          {/* Quick Info Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations rapides</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  {`\u00c9tat actuel`}
                </label>
                <div className="text-lg font-bold text-gray-900">
                  {`\u00c9tat ${data.state}`}
                </div>
              </div>
              {data.subStatus && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Sous-statut
                  </label>
                  <div className="text-lg font-semibold text-gray-900">
                    {data.subStatus}
                  </div>
                </div>
              )}
              {data.intervention_conseiller !== undefined && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Intervention conseiller
                  </label>
                  <div className={`text-lg font-semibold ${data.intervention_conseiller ? 'text-orange-600' : 'text-green-600'}`}>
                    {data.intervention_conseiller ? 'En cours' : 'Non'}
                  </div>
                </div>
              )}
              {data.state_before_intervention && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    {`\u00c9tat avant intervention`}
                  </label>
                  <div className="text-lg font-semibold text-gray-900">
                    {`\u00c9tat ${data.state_before_intervention}`}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pension Fund History Modal */}
      {selectedPensionFund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Historique - {selectedPensionFund.fund_name}
              </h3>
              <button
                onClick={() => setSelectedPensionFund(null)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {!pensionFundActivities || pensionFundActivities.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {`Aucun historique enregistr\u00e9 pour cette caisse de pension`}
                </div>
              ) : (
                <div className="space-y-4">
                  {pensionFundActivities.map((activity) => {
                    const getActivityIcon = (type: string) => {
                      switch (type) {
                        case 'status_changed': return '\uD83D\uDD04';
                        case 'field_changed': return '\u270F\uFE0F';
                        case 'document_uploaded': return '\uD83D\uDCE4';
                        case 'document_replaced': return '\uD83D\uDD04';
                        case 'document_deleted': return '\uD83D\uDDD1\uFE0F';
                        case 'created': return '\u2728';
                        default: return '\uD83D\uDCCB';
                      }
                    };

                    const getActivityTitle = (type: string) => {
                      switch (type) {
                        case 'status_changed': return 'Changement de statut';
                        case 'field_changed': return 'Modification du champ';
                        case 'document_uploaded': return "Document upload\u00e9";
                        case 'document_replaced': return "Document remplac\u00e9";
                        case 'document_deleted': return "Document supprim\u00e9";
                        case 'created': return "Caisse cr\u00e9\u00e9e";
                        default: return 'Action';
                      }
                    };

                    const getActivityColor = (type: string) => {
                      switch (type) {
                        case 'status_changed': return 'border-blue-200 bg-blue-50';
                        case 'field_changed': return 'border-purple-200 bg-purple-50';
                        case 'document_uploaded': return 'border-green-200 bg-green-50';
                        case 'document_replaced': return 'border-yellow-200 bg-yellow-50';
                        case 'document_deleted': return 'border-red-200 bg-red-50';
                        case 'created': return 'border-gray-200 bg-gray-50';
                        default: return 'border-gray-200 bg-gray-50';
                      }
                    };

                    return (
                      <div
                        key={activity.id}
                        className={`border-l-4 rounded-lg p-4 ${getActivityColor(activity.activity_type)}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="text-2xl">{getActivityIcon(activity.activity_type)}</div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {getActivityTitle(activity.activity_type)}
                            </div>
                            {activity.description && (
                              <div className="mt-1 text-sm text-gray-700">
                                {activity.description}
                              </div>
                            )}
                            {activity.field_name && (
                              <div className="mt-1 text-sm">
                                <span className="font-medium">Champ:</span> {activity.field_name}
                              </div>
                            )}
                            {(activity.old_value || activity.new_value) && (
                              <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                                {activity.old_value && (
                                  <div>
                                    <span className="font-medium text-red-700">Avant:</span>
                                    <div className="text-gray-700">{activity.old_value}</div>
                                  </div>
                                )}
                                {activity.new_value && (
                                  <div>
                                    <span className="font-medium text-green-700">{`Apr\u00e8s:`}</span>
                                    <div className="text-gray-700">{activity.new_value}</div>
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                              <span>
                                {'\uD83D\uDCC5 '}{new Date(activity.created_at).toLocaleString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                              {activity.user_name && (
                                <span>{'\uD83D\uDC64 '}{activity.user_name}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setSelectedPensionFund(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetails;
